import React, { useState } from 'react';
import { TextField } from '@mui/material';

interface NumberInputProps {
  value: number;
  label: string;
  onValueChange: (newValue: number) => void;
  onErrorChange?: (hasError: boolean) => void;
  min?: number;
  max?: number;
  width?: number;
  size?: 'small' | 'medium';
  required?: boolean;
  fullWidth?: boolean;
  disabled?: boolean;
}

const NumberInput: React.FC<NumberInputProps> = ({ 
  value, 
  label, 
  onValueChange, 
  onErrorChange,
  min = 1,
  max,
  width,
  size = 'medium',
  required = false,
  fullWidth = false,
  disabled = false
}) => {
  const [internalValue, setInternalValue] = useState(value.toString());
  const [isValid, setIsValid] = useState(true);

  // Handle change in number input
  const handleValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setInternalValue(newValue);

    // Validation
    const parsedValue = parseInt(newValue);
    let valid = true;
    
    if (newValue === '') {
      valid = !required;
    } else if (isNaN(parsedValue)) {
      valid = false;
    } else if (parsedValue < min) {
      valid = false;
    } else if (max && parsedValue > max) {
      valid = false;
    }

    setIsValid(valid);
    if (onErrorChange) {
      onErrorChange(!valid);
    }

    if (valid && !isNaN(parsedValue)) {
      onValueChange(parsedValue);
    }
  };

  // Handle blur to format the value
  const handleBlur = () => {
    const parsedValue = parseInt(internalValue);
    if (!isNaN(parsedValue)) {
      const clampedValue = Math.max(min, max ? Math.min(parsedValue, max) : parsedValue);
      setInternalValue(clampedValue.toString());
      onValueChange(clampedValue);
    } else if (required) {
      setInternalValue(min.toString());
      onValueChange(min);
    }
  };

  return (
    <TextField
      label={label}
      value={internalValue}
      onChange={handleValueChange}
      onBlur={handleBlur}
      error={!isValid}
      variant="outlined"
      size={size}
      type="number"
      required={required}
      fullWidth={fullWidth}
      disabled={disabled}
      sx={width ? { width } : undefined}
      InputProps={{
        inputProps: { 
          min,
          max,
          step: 1
        },
        sx: { textAlign: 'center' }
      }}
    />
  );
};

export default NumberInput; 